import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { BookOpen, Camera, Loader2, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * BookScanner — Admin can photograph pages of books to feed Destine IA
 * with high-quality knowledge (à la Anthropic). Each scan is OCR'd via Gemini
 * Vision and stored in db.dsia_knowledge for later retrieval.
 */
export default function BookScanner() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [bookTitle, setBookTitle] = useState('');
  const [bookAuthor, setBookAuthor] = useState('');
  const [scanning, setScanning] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/admin-ext/knowledge/list');
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const pick = () => inputRef.current?.click();

  const onChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    e.target.value = '';
  };

  const scan = async () => {
    if (!file) return;
    if (!bookTitle.trim()) { toast.error('Précisez le titre du livre'); return; }
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('book_title', bookTitle);
      fd.append('book_author', bookAuthor);
      await axios.post('/api/admin-ext/knowledge/scan-book-page', fd, { timeout: 120000 });
      toast.success('Page ajoutée à la connaissance de Destine IA');
      setFile(null); setPreview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan échoué');
    } finally { setScanning(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette page ?')) return;
    try {
      await axios.delete(`/api/admin-ext/knowledge/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="mb-4 p-4 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30" data-testid="book-scanner">
      <div className="flex items-start gap-3 mb-3">
        <BookOpen className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-amber-200 text-sm">Scanner de livres — nourrir Destine IA</h4>
          <p className="text-[11px] text-amber-200/70 mt-0.5 leading-snug">
            Photographiez une page. Le texte est extrait et ajouté à la base de connaissances de l&apos;IA.
          </p>
        </div>
      </div>

      <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={onChange} className="hidden" data-testid="book-scanner-input" />

      <div className="grid grid-cols-2 gap-2 mb-2">
        <input
          value={bookTitle} onChange={e => setBookTitle(e.target.value)}
          placeholder="Titre du livre *"
          className="bg-black/40 border border-amber-500/30 rounded-full px-3 py-2 text-xs"
          data-testid="book-scanner-title"
        />
        <input
          value={bookAuthor} onChange={e => setBookAuthor(e.target.value)}
          placeholder="Auteur (optionnel)"
          className="bg-black/40 border border-amber-500/30 rounded-full px-3 py-2 text-xs"
          data-testid="book-scanner-author"
        />
      </div>

      {preview && (
        <div className="relative mb-2">
          <img src={preview} alt="page" className="w-full max-h-48 object-contain rounded-xl bg-black/40" />
          <button onClick={() => { setFile(null); setPreview(null); }} className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70" data-testid="book-scanner-remove">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="flex gap-2">
        {!file ? (
          <button onClick={pick} className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 font-semibold text-xs flex items-center justify-center gap-2" data-testid="book-scanner-pick">
            <Camera className="w-4 h-4" /> Photographier une page
          </button>
        ) : (
          <button onClick={scan} disabled={scanning} className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 font-semibold text-xs flex items-center justify-center gap-2 disabled:opacity-50" data-testid="book-scanner-scan">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
            {scanning ? 'Extraction…' : 'Extraire et enregistrer'}
          </button>
        )}
      </div>

      {/* Existing knowledge list */}
      <div className="mt-3">
        <div className="text-[11px] font-semibold text-amber-200/80 mb-1">Pages enregistrées ({items.length})</div>
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin text-amber-300" />
        ) : items.length === 0 ? (
          <div className="text-[11px] text-amber-200/50">Aucune page pour le moment.</div>
        ) : (
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {items.map(it => (
              <div key={it.id} className="p-2 rounded-lg bg-black/30 border border-amber-500/20 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-amber-100 truncate">{it.book_title}</div>
                  <div className="text-[10px] text-amber-200/60 truncate">{it.book_author || '—'} · {(it.text || '').length} caractères</div>
                </div>
                <button onClick={() => del(it.id)} className="p-1.5 rounded-full bg-red-500/20 hover:bg-red-500/30" data-testid={`book-scanner-del-${it.id}`}>
                  <Trash2 className="w-3 h-3 text-red-300" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
